import EventEmitter, { Listener, OnOptions } from 'eventemitter2'
import { APIEvents, EventListenerFunctions } from './APIEvents'
import type APIRequest from './APIRequest'
import APIResponse from './APIResponse'
import type { URLParams, Headers, RequestData, ResponseData } from './APITypes'
import type InterfaceAPI from './InterfaceAPI'
import NetworkFetchError from '@/erros/NetworkFetchError'

class FetchAPI extends EventEmitter implements InterfaceAPI {
  constructor(public baseUrl?: string) {
    super()
  }
  async send<
    Theaders extends Headers | undefined,
    TData extends RequestData,
    TURLParams extends URLParams,
    TAPIRequest extends APIRequest<Theaders, TData, TURLParams>,
    TAPIResponse extends APIResponse<unknown, unknown, TAPIRequest>,
  >(request: TAPIRequest, middlewareOptions?: any): Promise<TAPIResponse> {
    await this.emitAsync(APIEvents.request, request, middlewareOptions || {})
    const { url, urlParams, method, data, headers } = request
    const baseFullUrl = this.baseUrl + url
    const urlWithParams = this.getUrlWithParams(baseFullUrl, urlParams)

    const [resultData, resultHeaders] = (() => {
      if (data instanceof FormData) {
        return [data, {}]
      } else {
        return [
          typeof data !== 'string' ? JSON.stringify(data) : data,
          { 'Content-Type': 'application/json' },
        ]
      }
    })()

    const response = await fetch(urlWithParams, {
      method,
      headers: Object.assign(headers || {}, resultHeaders),
      body: resultData,
      credentials: request.withCredentials ? 'include' : 'same-origin',
    })

    const responseData: ResponseData = await response.json()
    const responseHeaders = this.convertHeadersToPlainObject(response.headers)
    const apiResponse = new APIResponse({
      data: responseData,
      status: response.status,
      headers: responseHeaders,
      request: request,
    })

    await this.emitAsync(
      APIEvents.response,
      apiResponse,
      middlewareOptions || {},
    )
    if (response.status < 200 || response.status >= 400) {
      throw new NetworkFetchError(
        apiResponse as APIResponse,
        `Network Error : request to ${url}`,
      )
    }

    return apiResponse as TAPIResponse
  }
  private getUrlWithParams(url: string, urlParams?: URLParams): string {
    if (!urlParams) return url
    const urlObj = new URL(url)
    Object.entries(urlParams as Record<string, string>).forEach(
      ([key, value]) => {
        if (value) {
          urlObj.searchParams.append(key, value)
        }
      },
    )
    return urlObj.toString()
  }
  private convertHeadersToPlainObject(
    headers: globalThis.Headers,
  ): Record<string, string> {
    const plainObject: Record<string, string> = {}
    for (const key of Object.keys(headers)) {
      plainObject[key] = headers.get(key)!
    }
    return plainObject
  }

  // type override 를 위해서 구현 class 에서 메서드들을 재정의 해줘야함..
  addListener<T extends keyof typeof APIEvents>(
    event: T,
    listener: EventListenerFunctions[T],
  ): this | Listener {
    return super.addListener(event, listener)
  }
  on<T extends keyof typeof APIEvents>(
    event: T,
    listener: EventListenerFunctions[T],
    options?: boolean | OnOptions,
  ): this | Listener {
    return super.on(event, listener, options)
  }
  prependListener<T extends keyof typeof APIEvents>(
    event: T,
    listener: EventListenerFunctions[T],
    options?: boolean | OnOptions,
  ): this | Listener {
    return super.prependListener(event, listener, options)
  }
  once<T extends keyof typeof APIEvents>(
    event: T,
    listener: EventListenerFunctions[T],
    options?: true | OnOptions,
  ): this | Listener {
    return super.once(event, listener, options)
  }
  emit<T extends keyof typeof APIEvents>(
    event: T,
    ...values: Parameters<EventListenerFunctions[T]>
  ): boolean {
    return super.emit(event, ...values)
  }
  emitAsync<T extends keyof typeof APIEvents>(
    event: T,
    ...values: Parameters<EventListenerFunctions[T]>
  ): Promise<any[]> {
    return super.emitAsync(event, ...values)
  }
}

export default FetchAPI
